import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Package, PackageCheck, PackageMinus, Boxes, ArrowRight } from "lucide-react";
import AddWorkDialog from "@/components/AddWorkDialog";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, accent = "primary", testid }) => {
  const accents = {
    primary: "text-[#3B5249] bg-[#EFF1EE]",
    success: "text-[#5C7D64] bg-[#EBF1EC]",
    warning: "text-[#A85F26] bg-[#FBEEDF]",
    neutral: "text-[#5C605C] bg-[#F1F1EE]",
  };
  return (
    <Card className="rounded-2xl border border-[#E6E6E2] bg-white shadow-soft p-6 card-hover fade-up" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`h-11 w-11 rounded-xl grid place-items-center ${accents[accent]}`}>
          <Icon size={20} strokeWidth={1.75} />
        </div>
      </div>
      <div className="mt-6">
        <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#5C605C]">{label}</div>
        <div className="mt-1.5 font-display text-3xl font-semibold text-[#1A1D1A]">{value}</div>
      </div>
    </Card>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [byWorker, setByWorker] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);

  const load = async () => {
    try {
      const [s, b, w] = await Promise.all([
        api.get("/stats/overview"),
        api.get("/stats/by-worker"),
        api.get("/workers"),
      ]);
      setStats(s.data);
      setByWorker(b.data);
      setWorkers(w.data);
    } catch (e) {
      toast.error("Veriler yüklenemedi");
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-10">
      {/* Header */}
      <section className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 fade-up">
        <div>
          <div className="text-xs font-semibold tracking-[0.22em] uppercase text-[#D98359]">Genel Bakış</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A1D1A]">
            Bugünkü atölye durumu
          </h1>
          <div className="brand-underline mt-3 w-16" />
          <p className="mt-4 text-[#5C605C] max-w-xl">
            Verilen ve alınan işleri tek bakışta izleyin. Ütü ve paket için bekleyen adetleri kaçırmayın.
          </p>
        </div>
        <Button
          onClick={() => setOpenAdd(true)}
          className="rounded-full h-12 px-6 bg-[#3B5249] hover:bg-[#2C3E37] text-white shadow-soft transition-colors"
          data-testid="add-work-btn"
        >
          <Plus size={18} strokeWidth={2} className="mr-1.5" />
          Yeni İş Ekle
        </Button>
      </section>

      {/* Stat cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard icon={Boxes} label="Toplam İş" value={stats?.total_works ?? "-"} accent="primary" testid="stat-total-works" />
        <StatCard icon={Package} label="Devam Eden" value={stats?.in_progress ?? "-"} accent="warning" testid="stat-in-progress" />
        <StatCard icon={PackageCheck} label="Tamamlanan" value={stats?.completed ?? "-"} accent="success" testid="stat-completed" />
        <StatCard icon={PackageMinus} label="Bekleyen Adet" value={stats?.pending_quantity ?? "-"} accent="neutral" testid="stat-pending-qty" />
      </section>

      {/* Worker summary */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-semibold tracking-[0.22em] uppercase text-[#5C605C]">Çalışan Özeti</div>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl font-medium text-[#1A1D1A]">Kimde ne kadar iş var?</h2>
          </div>
          <Link to="/workers" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[#3B5249] hover:text-[#2C3E37] transition-colors" data-testid="all-workers-link">
            Tümü <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {byWorker.map((w, i) => (
            <Link
              key={w.worker_id}
              to={`/workers/${w.worker_id}`}
              className="group"
              data-testid={`worker-card-${w.worker_name.toLowerCase()}`}
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <Card className="rounded-2xl border border-[#E6E6E2] bg-white shadow-soft p-6 card-hover hover:shadow-soft-hover hover:border-[#D6D6CE] fade-up">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#3B5249] to-[#5C7D64] text-white grid place-items-center font-display font-semibold text-lg">
                    {w.worker_name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-semibold text-lg text-[#1A1D1A]">{w.worker_name}</div>
                    <div className="text-xs text-[#5C605C]">{w.total_works} toplam iş</div>
                  </div>
                  <ArrowRight size={18} className="text-[#5C605C] group-hover:text-[#3B5249] group-hover:translate-x-0.5 transition-transform" />
                </div>

                <div className="mt-5 grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-[#FBEEDF] px-3 py-2.5">
                    <div className="text-[10px] font-semibold tracking-widest uppercase text-[#A85F26]">Bekleyen</div>
                    <div className="mt-0.5 font-display text-xl font-semibold text-[#A85F26]">{w.pending_quantity}</div>
                  </div>
                  <div className="rounded-xl bg-[#EBF1EC] px-3 py-2.5">
                    <div className="text-[10px] font-semibold tracking-widest uppercase text-[#3F5B47]">Alınan</div>
                    <div className="mt-0.5 font-display text-xl font-semibold text-[#3F5B47]">{w.total_received}</div>
                  </div>
                  <div className="rounded-xl bg-[#EFF1EE] px-3 py-2.5">
                    <div className="text-[10px] font-semibold tracking-widest uppercase text-[#3B5249]">Verilen</div>
                    <div className="mt-0.5 font-display text-xl font-semibold text-[#3B5249]">{w.total_given}</div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      <AddWorkDialog open={openAdd} onOpenChange={setOpenAdd} workers={workers} onSuccess={load} />
    </div>
  );
}
